if (!customElements.get('osft-subscription-builder')) {
  customElements.define(
    'osft-subscription-builder',
    class OsftSubscriptionBuilder extends HTMLElement {
      connectedCallback() {
        this.strings = JSON.parse(this.querySelector('[data-strings]').textContent);
        this.max = parseInt(this.dataset.max, 10) || 10;
        this.freeShipping = this.toPresentment(parseInt(this.dataset.freeShipping, 10) || 0);
        this.step = 1;
        this.furthestStep = 1;

        this.form = this.querySelector('form');
        this.steps = [...this.querySelectorAll('.osft-sub__step')];
        this.backButton = this.querySelector('[data-back]');
        this.nextButton = this.querySelector('[data-next]');
        this.submitButton = this.querySelector('[data-submit]');
        this.summary = this.querySelector('[data-summary]');

        this.form.addEventListener('change', this.onChange.bind(this));
        this.form.addEventListener('input', this.onChange.bind(this));
        this.form.addEventListener('click', this.onClick.bind(this));
        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.querySelectorAll('[data-go-to]').forEach((button) =>
          button.addEventListener('click', () => this.goTo(parseInt(button.dataset.goTo, 10)))
        );

        this.onAnchorClick = this.onAnchorClick.bind(this);
        document.addEventListener('click', this.onAnchorClick);

        this.update();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onAnchorClick);
      }

      // Links to the builder (e.g. the benefits section's #subscribe button) scroll smoothly
      // and move focus to the first step, instead of jumping to the anchor
      onAnchorClick(event) {
        const link = event.target.closest('a[href^="#"]');
        if (!link || link.hash.length < 2) return;
        const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
        if (!target || !this.contains(target)) return;
        event.preventDefault();
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        history.replaceState(null, '', link.hash);
        this.steps[this.step - 1].querySelector('.osft-sub__step-heading').focus({ preventScroll: true });
      }

      // Money in the store currency (cents) to the shopper's currency
      toPresentment(cents) {
        const rate = parseFloat(window.Shopify?.currency?.rate || '1');
        return Math.round(cents * rate);
      }

      // Same output as Liquid's money filter, using the store's money format
      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '${{amount}}';
        const number = (value, decimals, thousands = ',', decimal = '.') => {
          const [whole, fraction] = (value / 100).toFixed(decimals).split('.');
          return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
        };
        return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, placeholder) => {
          switch (placeholder) {
            case 'amount_no_decimals':
              return number(cents, 0);
            case 'amount_with_comma_separator':
              return number(cents, 2, '.', ',');
            case 'amount_no_decimals_with_comma_separator':
              return number(cents, 0, '.', ',');
            case 'amount_with_apostrophe_separator':
              return number(cents, 2, "'", '.');
            default:
              return number(cents, 2);
          }
        });
      }

      format(template, values) {
        return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) => (key in values ? values[key] : match));
      }

      get selectedFlavors() {
        return [...this.querySelectorAll('.osft-sub__flavor-input:checked')].map((input) => input.value);
      }

      get frequencyInput() {
        return this.querySelector('input[name="frequency"]:checked');
      }

      // Selected lines: only sizes of selected flavors with a quantity above 0
      get lines() {
        const frequency = this.frequencyInput ? this.frequencyInput.value : '0';
        return this.selectedFlavors.flatMap((handle) =>
          [...this.querySelectorAll(`[data-flavor="${CSS.escape(handle)}"] .osft-sub__size`)]
            .map((row) => {
              const input = row.querySelector('.quantity__input');
              const quantity = input.disabled ? 0 : parseInt(input.value, 10) || 0;
              const [planId, planPrice] = (row.getAttribute(`data-plan-${frequency}`) || ':').split(':');
              return {
                row,
                label: row.dataset.label,
                spokenLabel: row.dataset.labelSpoken || row.dataset.label,
                variantId: parseInt(row.dataset.variantId, 10),
                quantity,
                price: parseInt(row.dataset.price, 10),
                planId: parseInt(planId, 10),
                planPrice: parseInt(planPrice, 10) || parseInt(row.dataset.price, 10),
              };
            })
            .filter((line) => line.quantity > 0)
        );
      }

      onClick(event) {
        const stepButton = event.target.closest('[data-step-quantity]');
        if (stepButton) {
          const input = document.getElementById(stepButton.getAttribute('aria-controls'));
          const value = (parseInt(input.value, 10) || 0) + parseInt(stepButton.dataset.stepQuantity, 10);
          input.value = Math.min(this.max, Math.max(0, value));
          this.hideErrors();
          this.update();
          return;
        }
        if (event.target.closest('[data-next]')) this.next();
        if (event.target.closest('[data-back]')) this.goTo(this.step - 1);
      }

      onChange(event) {
        const input = event.target;
        if (input.classList.contains('quantity__input') && event.type === 'change') {
          const value = parseInt(input.value, 10);
          input.value = Number.isNaN(value) ? 0 : Math.min(this.max, Math.max(0, value));
        }
        if (input.classList.contains('osft-sub__flavor-input') && input.checked) {
          this.preselectSize(input.value);
        }
        this.hideErrors();
        this.update();
      }

      // A newly chosen flavor starts with one bag of its largest available size (the last variant, Grande)
      preselectSize(handle) {
        const inputs = [...this.querySelectorAll(`[data-flavor="${CSS.escape(handle)}"] .quantity__input:not(:disabled)`)];
        if (inputs.length && inputs.every((input) => (parseInt(input.value, 10) || 0) === 0)) inputs.at(-1).value = 1;
      }

      // Selected flavors with no bags in any size
      get flavorsWithoutSize() {
        const lines = this.lines;
        return this.selectedFlavors
          .map((handle) => this.querySelector(`[data-flavor="${CSS.escape(handle)}"]`))
          .filter((fieldset) => !lines.some((line) => fieldset.contains(line.row)));
      }

      validate(step) {
        if (step === 1 && this.selectedFlavors.length === 0) return this.showError(1, this.strings.chooseFlavor);
        if (step === 2) {
          if (this.lines.length === 0) return this.showError(2, this.strings.chooseQuantity);
          const missing = this.flavorsWithoutSize;
          if (missing.length) {
            missing.forEach((fieldset) => fieldset.classList.add('is-missing'));
            const names = new Intl.ListFormat(document.documentElement.lang || 'en', { type: 'conjunction' }).format(
              missing.map((fieldset) => fieldset.dataset.flavorName)
            );
            return this.showError(2, this.format(this.strings.chooseSize, { flavors: names }));
          }
        }
        return true;
      }

      showError(step, message) {
        const error = this.querySelector(`.osft-sub__step[data-step="${step}"] .osft-sub__error`);
        error.textContent = message;
        error.hidden = false;
        return false;
      }

      hideErrors() {
        this.querySelectorAll('.osft-sub__flavor-sizes.is-missing').forEach((fieldset) => fieldset.classList.remove('is-missing'));
        this.querySelectorAll('.osft-sub__error').forEach((error) => {
          error.hidden = true;
          error.textContent = '';
        });
      }

      next() {
        if (!this.validate(this.step)) return;
        this.goTo(this.step + 1);
      }

      goTo(step) {
        if (step < 1 || step > this.steps.length) return;
        // Going forward past a step requires that step to be complete
        for (let i = this.step; i < step; i++) {
          if (!this.validate(i)) return this.goTo(i);
        }
        this.hideErrors();
        this.step = step;
        this.furthestStep = Math.max(this.furthestStep, step);
        this.steps.forEach((section) => (section.hidden = parseInt(section.dataset.step, 10) !== step));
        this.querySelectorAll('.osft-sub__flavor-sizes').forEach((fieldset) => {
          fieldset.hidden = !this.selectedFlavors.includes(fieldset.dataset.flavor);
        });
        this.update();

        const heading = this.steps[step - 1].querySelector('.osft-sub__step-heading');
        const top = this.querySelector('.osft-sub__progress').getBoundingClientRect().top;
        if (top < 0) this.querySelector('.osft-sub__progress').scrollIntoView({ behavior: 'smooth', block: 'start' });
        heading.focus({ preventScroll: true });
      }

      update() {
        const lines = this.lines;
        const count = lines.reduce((sum, line) => sum + line.quantity, 0);
        const total = lines.reduce((sum, line) => sum + line.quantity * line.planPrice, 0);
        const savings = lines.reduce((sum, line) => sum + line.quantity * (line.price - line.planPrice), 0);
        this.querySelectorAll('.osft-sub__size').forEach((row) =>
          row.classList.toggle('has-quantity', (parseInt(row.querySelector('.quantity__input').value, 10) || 0) > 0)
        );

        // Progress: completed steps can be revisited; later ones unlock as the shopper reaches them
        this.querySelectorAll('.osft-sub__progress-item').forEach((item) => {
          const itemStep = parseInt(item.dataset.step, 10);
          const button = item.querySelector('button');
          button.disabled = itemStep > this.furthestStep || itemStep === this.step;
          item.classList.toggle('is-complete', itemStep < this.step);
          if (itemStep === this.step) item.setAttribute('aria-current', 'step');
          else item.removeAttribute('aria-current');
        });

        this.backButton.hidden = this.step === 1;
        this.nextButton.hidden = this.step === this.steps.length;
        this.submitButton.hidden = this.step !== this.steps.length;
        this.summary.hidden = this.step === 1 || count === 0;

        this.querySelector('[data-summary-count]').textContent = this.format(
          count === 1 ? this.strings.bagsOne : this.strings.bagsOther,
          { count }
        );
        this.querySelector('[data-summary-total]').textContent = this.format(this.strings.perDelivery, {
          total: this.formatMoney(total),
        });
        this.querySelector('[data-summary-savings]').textContent =
          savings > 0 ? this.format(this.strings.savings, { amount: this.formatMoney(savings) }) : '';

        const shippingText = this.querySelector('[data-shipping-text]');
        if (shippingText && this.freeShipping > 0) {
          const remaining = this.freeShipping - total;
          shippingText.textContent =
            remaining > 0
              ? this.format(this.strings.shippingRemaining, { amount: this.formatMoney(remaining) })
              : this.strings.shippingFree;
          this.querySelector('[data-shipping-bar]').style.width = `${Math.min(100, (total / this.freeShipping) * 100)}%`;
          shippingText.parentElement.classList.toggle('is-free', remaining <= 0);
        }

        if (this.step === this.steps.length) this.renderReview(lines);
      }

      renderReview(lines) {
        const list = this.querySelector('[data-review-list]');
        list.replaceChildren(
          ...lines.map((line) => {
            const item = document.createElement('li');
            item.className = 'osft-sub__review-item';
            // Shown as "1 × Original Beef Jerky, Grande (16oz)", read as "... 16 ounces"
            const name = document.createElement('span');
            const shown = document.createElement('span');
            shown.setAttribute('aria-hidden', 'true');
            shown.textContent = `${line.quantity} × ${line.label}`;
            const spoken = document.createElement('span');
            spoken.className = 'visually-hidden';
            spoken.textContent = `${line.quantity} × ${line.spokenLabel}`;
            name.append(shown, spoken);
            const price = document.createElement('span');
            price.textContent = this.formatMoney(line.quantity * line.planPrice);
            item.append(name, price);
            return item;
          })
        );
        const frequency = this.frequencyInput;
        this.querySelector('[data-review-frequency]').textContent = frequency
          ? this.format(this.strings.reviewFrequency, { frequency: frequency.dataset.label })
          : '';
      }

      async onSubmit(event) {
        event.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        if (this.step !== this.steps.length) return this.next();
        const lines = this.lines;
        if (!lines.length) return this.goTo(2);

        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        this.submitButton.querySelector('.loading__spinner').classList.remove('hidden');
        this.hideErrors();

        const root = window.Shopify?.routes?.root || '/';
        try {
          // The checkout should contain only this subscription
          const clear = await fetch(`${root}cart/clear.js`, { method: 'POST', headers: { Accept: 'application/json' } });
          if (!clear.ok) throw new Error();
          const response = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: lines.map((line) => ({
                id: line.variantId,
                quantity: line.quantity,
                selling_plan: line.planId,
              })),
            }),
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.description || data.message);
          window.location.href = `${root}checkout`;
        } catch (error) {
          this.showError(4, error.message ? `${this.strings.addError} ${error.message}` : this.strings.addError);
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.classList.remove('loading');
          this.submitButton.querySelector('.loading__spinner').classList.add('hidden');
        }
      }
    }
  );
}
